import type { Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useProfileStore } from '@entities/viewer'

import { useApiTokensStore } from './store'
import { useApiTokensApi } from '../../api/api-tokens/list.hook'

interface FiltersApiTokensReturn {
  refUserId: Ref<number | undefined>
  refSearch: Ref<string>
  userOptions: ComputedRef<AppSelectOption<number>[]>
  canFilterByUser: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  submitSearch: () => Promise<void>
  openCreateModal: () => void
}

/** The controls hold a draft; nothing is requested until "Search" or Enter in the search input. */
export const useFiltersApiTokens = (): FiltersApiTokensReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useApiTokensStore()
  const profileStore = useProfileStore()

  const refUserId = ref<number | undefined>(undefined)
  const refSearch = ref<string>('')

  const userOptions = computed<AppSelectOption<number>[]>(() => store.getUserOptions)
  const canFilterByUser = computed<boolean>(() =>
    profileStore.privileges?.includes('manage-all-api-tokens') ?? false,
  )
  const isLoading = computed<boolean>(() => store.isLoading)

  const syncDraft = (): void => {
    refUserId.value = store.getFilterUserId ?? undefined
    refSearch.value = store.getFilterSearch ?? ''
  }

  /** `page` is dropped so a new filter never lands on a page that no longer exists. */
  const submitSearch = async (): Promise<void> => {
    store.setFilterUserId(refUserId.value ?? null)
    store.setFilterSearch(refSearch.value.trim() || null)

    await router.push({
      query: {
        ...route.query,
        user_id: store.getFilterUserId ? String(store.getFilterUserId) : undefined,
        search: store.getFilterSearch || undefined,
        page: undefined,
      },
    })
    await useApiTokensApi()
  }

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  // The store, not the URL: the list hydrates it from the query, and the draft follows once it has.
  watch(
    () => [
      store.getFilterUserId,
      store.getFilterSearch,
    ],
    syncDraft,
    { immediate: true, deep: true },
  )

  return {
    refUserId,
    refSearch,
    userOptions,
    canFilterByUser,
    isLoading,
    submitSearch,
    openCreateModal,
  }
}
